import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AccountCell, TextCell } from 'components/Cells/Cells';
import { SingleTicket } from 'utils/api/models/tickets/singleTicket';
import AccountFilterTable, { ISelectedAccount } from 'views/common/AccountFilterTable';
import { translate } from 'components/Translation';
import Dropdown from 'components/Dropdown/Dropdown';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { FilterValue } from 'react-table';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { finalize } from 'rxjs/operators';
import { AccountsService } from 'services/accounts/accounts';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { MoreVert, Business, Add } from '@material-ui/icons';
import styles from 'assets/jss/material-dashboard-pro-react/views/purchaseDeviceHistory/purchaseDeviceHistoryStyle';
import { makeStyles } from '@material-ui/core';
import { OrderTrackingService } from 'services/orderTracking/orderTracking';
import _ from 'lodash';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import OrderDetailsDialog from './subcomponents/OrderDetailsDialog';
import { PurchaseHistoryOrder } from 'utils/api/models/eshop/purchaseHistoryOrder';
import { useHistory } from 'react-router';

enum OrderEnum {
	G = 'PENDING',
	S = 'COMPLETED',
	X = 'CANCELLED',
	C = 'PAYMENT_COMPLETED',
	P = 'PAYMENT_COMPLETED',
	M = 'PAYMENT_COMPLETED',
	H = 'PAYMENT_COMPLETED',
}

const ORDERS_PER_PAGE = 30;

// @ts-ignore
const useStyles = makeStyles(styles);

interface IIndexProps {
	match: {
		path: string;
	};
}

const PurchaseDeviceHistoryList = ({ match }: IIndexProps) => {
	const history = useHistory();
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [selectedAccount, setSelectedAccount] = useState<ISelectedAccount | null>(null);
	const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [filters, setFilters] = useState<FilterValue>({
		period: ['lastDays'],
		status: [],
	});
	const [deviceOrders, setDeviceOrders] = useState<PurchaseHistoryOrder[]>([]);
	const [filteredOrders, setFilteredOrders] = useState<PurchaseHistoryOrder[]>([]);
	const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(10);
	const [eshopCurrentPage, setEshopCurrentPage] = useState<number>(1);

	const dispatch = useDispatch();
	const classes = useStyles();

	const filter = useCallback(
		_.debounce(() => {
			const _searchInputValue = searchInputValue.toLowerCase();
			const filtered = deviceOrders.filter((order: PurchaseHistoryOrder) => {
				return [
					[
						_searchInputValue !== '',
						['status', 'price', 'quantity', 'orderId']
							.filter((field) => !!order[field])
							.some((field) => {
								return order[field]
									.toString()
									.toLowerCase()
									.includes(_searchInputValue);
							}),
					],
				]
					.filter((check) => check[0])
					.every((check) => check[1]);
			});
			setFilteredOrders(filtered);
		}, 100),
		[searchInputValue, filters, selectedAccount, deviceOrders]
	);

	useEffect(() => {
		filter();
	}, [filter]);

	const accountsMap = useMemo(
		() => accounts.reduce((acc, account) => ({ ...acc, [account.accountId]: account }), {}),
		[accounts]
	);

	useEffect(() => {
		setLoadingAccounts(true);
		const subscription = AccountsService.getCorpAccountsForCurrentUser()
			.pipe(finalize(() => setLoadingAccounts(false)))
			.subscribe(({ accounts }) => {
				setAccounts(accounts);
			});
		return () => {
			subscription.unsubscribe();
		};
	}, []);

	useEffect(() => {
		if (!loadingAccounts) {
			dispatch(showLoadingIndicator());
			const subscription = OrderTrackingService.getPurchaseDeviceHistory(eshopCurrentPage, ORDERS_PER_PAGE)
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.subscribe((response) => {
					if (response.order) {
						if (currentPage === 1) {
							if (response.order.length === 0) {
								history.push(`${match.path}/new`);
							}
							setDeviceOrders(response.order);
						} else {
							setDeviceOrders((oldOrders) => [...oldOrders, ...response.order]);
						}
					}
				});
			return () => {
				subscription.unsubscribe();
			};
		}
	}, [selectedAccount, loadingAccounts, accounts, dispatch, eshopCurrentPage]);

	const getQuantity = (x_orderItems: string) => {
		const jsonOrderItems = x_orderItems ? JSON.parse(x_orderItems) : [];
		if (Array.isArray(jsonOrderItems)) {
			return jsonOrderItems.length;
		} else {
			return 0;
		}
	};

	return (
		<div style={{ width: '100%' }}>
			<AccountFilterTable
				accounts={accounts}
				setAccount={(value) => setSelectedAccount(value)}
				selectedAccount={selectedAccount}
				filterHeader={''}
				filtersValue={filters}
				onPaggingChange={(event: any) => {
					setCurrentPage(event);
					if ((event + 1) * pageSize >= deviceOrders.length) {
						setEshopCurrentPage(Math.floor(deviceOrders.length / ORDERS_PER_PAGE) + 1);
					}
				}}
				onPageSizeChange={(event: any) => {
					setPageSize(event);
					if ((currentPage + 1) * event >= deviceOrders.length) {
						setEshopCurrentPage(Math.floor(deviceOrders.length / ORDERS_PER_PAGE) + 1);
					}
				}}
				filters={[]}
				onFilterChange={(name, value) => {
					setFilters((filters: any) => ({ ...filters, [name]: value }));
				}}
				buttons={[
					{
						text: <>{translate('path.faq')}</>,
						onClick: () => history.push(`${match.path}/faq`),
					},
					{
						text: (
							<>
								<Add />
								{translate('purchase.bulk.devices.order.create')}
							</>
						),
						onClick: () => history.push(`${match.path}/new`),
					},
				]}
				data={filteredOrders}
				selectPanel={{}}
				columns={[
					{
						Header: translate('purchase.hisotry.device.orderId'),
						Cell: ({ original }: { original: PurchaseHistoryOrder }) => (
							<TextCell bold text={original.orderId} />
						),
						accessor: 'orderId',
					},
					{
						Header: translate('table.user.performedBy'),
						Cell: ({ original }: { original: PurchaseHistoryOrder }) => (
							<TextCell bold text={`${original.x_firstName} ${original.x_lastName}`} />
						),
						accessor: 'orderId',
					},
					!selectedAccount?.id && {
						Header: translate('table.organization.name'),
						Cell: ({ original }: { original: SingleTicket }) => {
							const account = accountsMap[original.createdBy];
							return account ? (
								<AccountCell
									text={account.accountName}
									icon={
										<CustomAvatar
											accountId={account.accountId}
											alt="Company Logo"
											placeholderIcon={<Business />}
										/>
									}
								/>
							) : null;
						},
						accessor: 'account.accountName',
					},
					{
						Header: translate('purchase.history.device.quantity'),
						Cell: ({ original }: { original: PurchaseHistoryOrder }) => (
							<TextCell bold text={getQuantity(original.x_orderItems)} />
						),
						accessor: 'x_orderItems',
					},
					{
						Header: translate('purchase.history.device.price'),
						Cell: ({ original }: { original: PurchaseHistoryOrder }) => (
							<TextCell bold text={translate('price', original.grandTotal)} />
						),
						accessor: 'grandTotal',
					},
					{
						Header: translate('purchase.history.device.status'),
						Cell: ({ original }: { original: PurchaseHistoryOrder }) => (
							<TextCell
								bold
								text={translate(
									`purchase.history.device.order.status.${OrderEnum[original.orderStatus]}`
								)}
							/>
						),
						accessor: 'orderStatus',
					},
					{
						Header: translate('purchase.history.device.ordered.date'),
						Cell: ({ original }: { original: PurchaseHistoryOrder }) => (
							<TextCell
								text={moment(original.placedDate)
									.format('lll')
									.toString()}
							/>
						),
						accessor: 'lastUpdateDate',
					},
					{
						width: 84,
						Cell: ({ original }: { original: PurchaseHistoryOrder }) => (
							<ConditionalRender
								show={!!original.x_shipmentNumber}
								onTrue={() => (
									<div className={classes.moreWrapper}>
										<Dropdown
											menu={<MoreVert />}
											items={[
												{
													text: translate('purchase.history.device.details'),
													onClick: () => {
														setSelectedOrderId(original.orderId);
													},
													hidden: false,
												},
											]}
											anchorOrigin={{
												vertical: 'bottom',
												horizontal: 'right',
											}}
											transformOrigin={{
												vertical: 'top',
												horizontal: 'right',
											}}
										/>
									</div>
								)}
							/>
						),
					},
				].filter(Boolean)}
				setSearchInputValue={setSearchInputValue}
				searchInputValue={searchInputValue}
				loading={false}
			/>
			<ConditionalRender
				show={!!selectedOrderId}
				onTrue={() => (
					<OrderDetailsDialog
						orderId={selectedOrderId ? selectedOrderId : ''}
						hideDialog={() => setSelectedOrderId(null)}
					/>
				)}
			/>
		</div>
	);
};

export default PurchaseDeviceHistoryList;
